import QrCodeIcon from '@mui/icons-material/QrCode';
import { Button,Dialog,DialogActions,DialogContent,DialogTitle,Stack } from '@mui/material';
import { useCallback,useEffect,useMemo,useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import AttToken from '../../../helpers/attToken';
import QrCode from '../../../service/QrCode';
import type { CreateUpdateBody,ModeloQRCode,RespostasModeloType } from '../../../types/checklist/types';
import type { ChecklistReportType } from '../../../types/reports/types';
import RenderQrCode from '../RenderQrCode';

type params = {
  identificador: { nome: string, resposta: string },
  id: string,
  openModal: boolean,
  setOpenModal: Function,
  selected: ChecklistReportType
};

export default function ModalQRCode({ id, identificador, openModal, setOpenModal, selected }: params) {
  const [isLoadingQrCode, setIsLoadingQrCode] = useState(true);
  const [qrCodeData, setQrCodeData] = useState<string>();
  const { t } = useTranslation('translation');

  const qrCodeService = useMemo(() => new QrCode(), []);

  if (!selected.modeloQrCode) {
    toast.warn(`${t('Reports.qrCodeNotConfigured')}`);
    setOpenModal(false);
  }

  const fetchQrCode = useCallback(async () => {
    setIsLoadingQrCode(true);
    const token = await AttToken();
    if (token) {
      const qrCode = await qrCodeService.getById({ ...token, formId: id, identificador: identificador.resposta });
      if (qrCode && qrCode.deepLink) {
        setQrCodeData(qrCode.deepLink);
      }
    }
    setIsLoadingQrCode(false);
  }, [id, identificador, qrCodeService]);

  const findRespostasModelo: (checklist: ChecklistReportType) => RespostasModeloType | null = (checklist: ChecklistReportType) => {
    if (!checklist.modeloQrCode) {
      return null;
    }
    const respostasModelo: RespostasModeloType = {};

    Object.keys(checklist.modeloQrCode).forEach((key: string) => {
      const splitedKey = key.split("||");
      const itemIndex = Number(splitedKey[0]);
      const subitemIndex = Number(splitedKey[1]);
      if (!isNaN(itemIndex) && !isNaN(subitemIndex)) {
        const subItem = checklist?.itens?.[itemIndex]?.subitens[subitemIndex];
        if (subItem && subItem.checked) {
          respostasModelo[key] = {
            subitemId: subitemIndex,
            itemId: itemIndex,
            tipo: subItem.tipo,
          };
          if (subItem.img) respostasModelo[key].foto = subItem.img;
          if (subItem.tipo === 'avaliacao') {
            respostasModelo[key].nota = subItem.nota;
            respostasModelo[key].comentario = subItem.ocorrencia || null;
          } else if (['textoCurto', 'textoLongo', 'data', 'hora', 'numero', 'email', 'telefone', 'rg', 'cpfcnpj', 'cep', 'placa'].includes(subItem.tipo)) {
            respostasModelo[key].ocorrencia = subItem.ocorrencia;
            respostasModelo[key].comentario = subItem.obs || null;
          } else if (['unique', 'list'].includes(subItem.tipo)) {
            respostasModelo[key].selecionados = subItem.selecionados as string[] | boolean[] | null | undefined;
            respostasModelo[key].comentario = subItem.obs || null;
          } else if (subItem.tipo === 'multiple') {
            respostasModelo[key].selecionados = subItem.selecionados as string[] | boolean[] | null | undefined;
            respostasModelo[key].comentario = subItem.ocorrencia || null;
          } else if (subItem.tipo === 'assinatura') {
            respostasModelo[key].ocorrencia = subItem.assinatura;
            respostasModelo[key].comentario = subItem.obs || null;
          }
        }
      }
    });

    if (respostasModelo) {
      return respostasModelo;
    }
    return null;
  };

  const getQrCodeObject: (checklist: ChecklistReportType) => CreateUpdateBody | null = (checklist: ChecklistReportType) => {
    const respostasModelo = findRespostasModelo(checklist);
    if (respostasModelo) {
      const objToSend: CreateUpdateBody = {
        nome: `${identificador.nome}: ${identificador.resposta}`,
        id: identificador.resposta,
        modeloId: id,
        respostasModelo
      };
      return objToSend;
    }
    return null;
  };


  const createQrCode = async (updateBody: CreateUpdateBody) => {
    await toast.promise(async () => {
      setIsLoadingQrCode(true);
      const token = await AttToken();
      if (token) {
        const qrCodeData = await qrCodeService.createUpdateQrCode({ ...token, ...updateBody });
        setQrCodeData(qrCodeData.deepLink);
      }
      setIsLoadingQrCode(false);
    }, { pending: `${t('Reports.creatingQRCode')}`, success: `${t('Reports.qrCodeCreated')}`, error: `${t('Reports.failedToCreateQRCode')}` });
  };

  useEffect(() => {
    if (isLoadingQrCode) {
      fetchQrCode();
    }
  }, [fetchQrCode, isLoadingQrCode]);

  return (
    <Dialog
      fullWidth
      maxWidth='md'
      open={openModal}
      onClose={() => setOpenModal(false)}
      scroll={'paper'}
    >
      <DialogTitle id="scroll-dialog-title">QR Code</DialogTitle>
      <DialogContent dividers={true}>
        <Stack direction='column' alignItems='center' justifyContent='flex-start' spacing={1} width='100%'>
          {isLoadingQrCode && (
            <Stack direction='column' justifyContent='flex-start' alignItems='center' spacing={1} width='100%'>
              Buscando QR Code...
            </Stack>
          )}
          {!isLoadingQrCode && !getQrCodeObject(selected) && (
            <h1>{t('Reports.formCannotGeneratePreFilledQRCodes')}</h1>
          )}
          {!isLoadingQrCode && (
            <Stack direction='column' justifyContent='flex-start' alignItems='center' spacing={1} width='100%'>
              {!qrCodeData && <span className='tag is-warning'>{t('Reports.noPreFilledQRCodeForIdentifier')}</span>}
              {qrCodeData && getQrCodeObject(selected) && (
                <RenderQrCode
                  qrCodeData={qrCodeData}
                  renderBody={getQrCodeObject(selected) as CreateUpdateBody}
                  modeloQrCode={selected.modeloQrCode as ModeloQRCode}
                />
              )}
              <Button
                className='button is-big is-black'
                onClick={async () => {
                  await createQrCode(getQrCodeObject(selected) as CreateUpdateBody);
                }}
              >
                {`${t('Reports.generate')}${qrCodeData ? `${t('Reports.another')}` : ''} QR Code`}&nbsp;&nbsp;
                <QrCodeIcon />
              </Button>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setOpenModal(false)}
          disabled={isLoadingQrCode}
        >
          {t('Reports.back')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
